import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErroDaApi } from './api.js';
import { enderecoDeEntrar } from './destino.js';
import { lerTranca, type TrancaDeOrdem } from './tranca.js';
import { lerLancamento, type Lancamento } from './lancamento.js';
import { useT } from './idioma.js';

/**
 * Carregar uma tela do aluno — e a ida ao login quando não há sessão.
 *
 * ## Por que este hook mora aqui, e não dentro do `App.tsx`
 *
 * Porque ele **é** a regra de "sem sessão, vá para a tela de entrar da área,
 * levando o destino". Enquanto ele foi uma função privada do `App.tsx`, as telas
 * que nasceram fora daquele arquivo — a agenda e o mural — buscaram sozinhas,
 * com um `.catch(setErro)`, e o resultado foi visível em produção: abrir
 * `/a/<slug>/comunidade` deslogado desenhava a casca inteira (título, subtítulo,
 * o botão "Escrever") e escrevia **"Entre para continuar."** como uma linha
 * solta no meio da página — a frase que o servidor manda no 401, exibida como se
 * fosse conteúdo.
 *
 * O `/a/<slug>` ao lado redirecionava certo, porque ele usava este hook. Um
 * arquivo que precisa ser copiado para estar correto vai divergir; então ele
 * saiu de lá e passou a ser importável.
 *
 * ## O que ele faz com cada recusa
 *
 * - **401**: navega para `/entrar?a=<slug>&destino=<caminho>`, com `replace` —
 *   o botão "voltar" do navegador não deve trazer a pessoa de volta a uma tela
 *   que ela não pode ver. O slug vem do ROUTER (`useParams`), nunca de
 *   `window.location.pathname`: o router é montado com `basename="/aluno"`, e
 *   fatiar o caminho do navegador pega o pedaço errado.
 * - **`curso_nao_comprado`**: não é um beco. Com endereço declarado, a tela
 *   desenha o convite; **sem** endereço, desenha a frase que diz que o curso
 *   não está no acesso desta pessoa, com a porta de volta à vitrine — e é por
 *   isso que o ramo já não exige o `oferta` ser texto. Ver `oferta`, abaixo.
 * - **o resto**: vira `erro`, e a tela decide como mostrá-lo.
 */
interface Guardado<T> {
  /** A leitura a que este resultado pertence. Ver `A CHAVE` abaixo. */
  chave: string;
  dados: T | null;
  erro: string | null;
  /*
   * ── O CURSO QUE NÃO É SEU, COM OU SEM PARA ONDE IR (02/10/2026) ──────────
   *
   * `url` e `titulo` passaram a ser anuláveis porque a recusa passou a existir
   * em duas formas. Ordem do dono: *«na ausência do link uma tela dizendo que
   * ele não tem acesso ao curso»* — e até aqui essa ausência caía no `erro`
   * genérico, que desenha a porta «Entrar» de `Recusa`: um convite a fazer
   * login a quem já está logado, sobre um curso que é dela em tudo menos no
   * acesso.
   *
   * **`titulo` só chega com `url`**, e isso é do servidor, não desta linha: sem
   * oferta declarada o 403 não nomeia o curso, para a recusa não servir de
   * sonda de catálogo a quem varre slugs. Ver `exigirCurso`, em
   * `escopo-aluno.ts`.
   */
  oferta: { url: string | null; titulo: string | null } | null;
  /*
   * A aula trancada por uma AVALIAÇÃO (0282): o servidor manda a prova que
   * destranca em `detalhes.avaliacao`, e a tela desenha a porta em vez da
   * parede — o mesmo gesto de `curso_nao_comprado` com a oferta.
   */
  tranca: {
    frase: string;
    avaliacao: { id: string; title: string } | null;
    /*
     * A aula trancada por um PRÉ-REQUISITO (24/09/2026): o FATO ao lado da
     * frase, para a recusa desenhar a distância em vez de só a nomear.
     *
     * A frase continua a ser a do servidor — ela é o título desta tela, e
     * rederigi-la aqui seria a segunda redação da mesma porta.
     */
    dependencia: TrancaDeOrdem | null;
    /*
     * A aula de um curso que AINDA NÃO ABRIU (24/09/2026) — `motivo:
     * 'hora_marcada'` no 403, com `liberaEm`.
     *
     * É a razão mais de fora de todas, e o servidor manda-a **sem**
     * `preRequisito` e sem `avaliacao` de propósito: não há nada a cumprir, e
     * um campo vazio ao lado convidaria a tela a desenhar uma régua que não
     * existe. Aqui ela serve para duas coisas que a frase sozinha não dá: dizer
     * que quem não abriu é o CURSO (não esta aula), e refazer a frase no fuso
     * de quem lê — a do 403 vem contada no relógio do servidor, que é UTC.
     */
    lancamento: Lancamento | null;
  } | null;
}

export function useCarregar<T>(carregar: () => Promise<T>, deps: unknown[]) {
  const t = useT();
  /*
   * ── A CHAVE: o resultado sabe a que leitura pertence (`AL7`) ─────────────
   *
   * ## O defeito que ela fecha, e por que o conserto anterior não bastou
   *
   * Já houve um conserto para isto, e está escrito no efeito abaixo: limpar o
   * estado ao trocar de rota, para nenhuma tela combinar `dados` velhos com
   * `useParams()` novos. **Mas `setDados(null)` acontece dentro de um efeito, e
   * um efeito corre DEPOIS do render.** Entre a mudança da URL e a limpeza há
   * exactamente um render em que os dois discordam — e um render chega para
   * montar um filho, e um filho montado chega para ele disparar o seu pedido.
   *
   * O dono voltou a medi-lo em produção, com o console aberto, ao abrir uma
   * aula de PDF:
   *
   * ```
   * GET /a/<slug>/aulas/21613be5-…/player  404
   * Uncaught (in promise) ErroDaApi: Esta aula não tem vídeo.
   * ```
   *
   * Conferido no banco: as duas aulas envolvidas são `kind = 'pdf'` e
   * `player_video_id` é **nulo em todas as aulas não-vídeo desta instalação**.
   * Logo o player não nasceu de `temVideo` — nasceu do `kind` da aula ANTERIOR,
   * no render em que `dados` ainda era dela e o `aulaId` já era o novo.
   *
   * ## Por que a chave e não mais um `setEstado(null)`
   *
   * Porque a limpeza por efeito é sempre tardia por construção: ela conserta o
   * segundo render, e o pedido já saiu no primeiro. *Um estado que pode estar
   * errado durante um render é um estado que vai ser lido errado* — e aqui foi.
   *
   * A chave resolve-o **durante o render**: o resultado carrega consigo a
   * leitura de que veio, e o que não corresponde à leitura corrente não é
   * devolvido. Não há instante em que a tela veja dados de outra aula.
   *
   * `String(d)` chega porque toda `deps` desta casa é texto ou número (slug,
   * id, ordem, página, versão) — conferido nos seis chamadores. Um objecto ali
   * daria `[object Object]` e a chave deixaria de mudar; se algum dia for
   * preciso, é este o sítio de o resolver, e não o dos chamadores.
   */
  const chave = deps.map((d) => String(d)).join('\u0000');

  const [guardado, setGuardado] = useState<Guardado<T> | null>(null);
  const vazio: Omit<Guardado<T>, 'chave'> = {
    dados: null,
    erro: null,
    oferta: null,
    tranca: null,
  };
  const atual = guardado?.chave === chave ? guardado : null;
  const dados = atual?.dados ?? null;
  const erro = atual?.erro ?? null;
  const oferta = atual?.oferta ?? null;
  const tranca = atual?.tranca ?? null;

  const setDados = (d: T) => setGuardado({ chave, ...vazio, dados: d });
  const setErro = (e: string | null) =>
    setGuardado(e === null ? null : { chave, ...vazio, erro: e });
  const setOferta = (o: Guardado<T>['oferta']) => setGuardado({ chave, ...vazio, oferta: o });
  const setTranca = (t: Guardado<T>['tranca']) => setGuardado({ chave, ...vazio, tranca: t });

  const navegar = useNavigate();
  const { areaSlug } = useParams();
  /* `useLocation` e não `window.location` pelo mesmo motivo do `useParams`: aqui
     o caminho já vem sem o `/aluno`, que é a forma em que o router navega. */
  const local = useLocation();

  useEffect(() => {
    let vivo = true;

    /*
     * O estado da tela ANTERIOR não sobrevive à troca de rota.
     *
     * Sem esta linha, `dados` continuava sendo o da aula que a pessoa acabou de
     * deixar enquanto a nova carregava — e as telas leem `dados` e `useParams()`
     * juntos. O efeito, medido em produção: sair de uma aula de vídeo e entrar
     * numa de TEXTO deixava `ConteudoDaAula` decidindo pelo `kind` antigo
     * (`video`) com o `aulaId` novo, montava o player e pedia
     * `GET /aulas/<a de texto>/player` — que responde 404 com razão, e a
     * rejeição não tinha quem a tratasse. Um `ErroDaApi: Esta aula não tem
     * vídeo.` no console **por entrada em aula de texto**, e uma requisição
     * inútil disparada no mesmo milissegundo da que traria a resposta certa.
     *
     * O conserto é aqui e não em `ConteudoDaAula` porque o defeito não é dela:
     * qualquer tela que combine dados carregados com parâmetros da URL tem o
     * mesmo problema, e consertar caso a caso deixa o próximo por consertar.
     * O preço é um "Carregando…" onde antes ficava o conteúdo velho — que é a
     * verdade: a tela nova ainda não chegou.
     */
    /*
     * **A limpeza saiu daqui, e é a coisa toda do `AL7`.**
     *
     * Estavam aqui quatro `setEstado(null)`, com o comentário acima a explicar
     * porquê. Eles consertavam o SEGUNDO render; o pedido inútil saía no
     * primeiro, que é o render em que `dados` e `useParams()` discordam. Agora
     * quem responde é a chave, durante o render, e não há discordância nenhuma
     * para limpar.
     *
     * O preço continua a ser o mesmo, e continua a ser a verdade: um
     * «Carregando…» onde antes ficava o conteúdo velho.
     */

    void carregar()
      .then((d) => vivo && setDados(d))
      .catch((e: unknown) => {
        if (!vivo) return;
        if (e instanceof ErroDaApi && e.precisaEntrar) {
          navegar(enderecoDeEntrar(areaSlug, `${local.pathname}${local.search}`), {
            replace: true,
          });
          return;
        }
        if (e instanceof ErroDaApi && e.codigo === 'curso_nao_comprado') {
          const d = e.detalhes ?? {};
          /*
           * Entra SEMPRE, com ou sem endereço — e é a mudança de 02/10/2026.
           *
           * Antes, o corpo sem `oferta` caía para baixo e virava `erro`: a tela
           * desenhava a porta «Entrar», que é o gesto errado para quem já
           * entrou. Agora a espécie da recusa é decidida pelo CÓDIGO do erro,
           * que o servidor manda nos dois casos, e o endereço é só o que muda
           * o botão.
           *
           * `titulo: null` em vez de «este curso»: a tela escreve a frase sem
           * nome quando não há nome, em vez de inventar um sujeito genérico
           * para a cabeça da página. Ver `SemAcessoAoCurso`, em `App.tsx`.
           */
          setOferta({
            url: typeof d.oferta === 'string' && d.oferta !== '' ? d.oferta : null,
            titulo: typeof d.titulo === 'string' && d.titulo !== '' ? d.titulo : null,
          });
          return;
        }
        if (e instanceof ErroDaApi && e.codigo === 'aula_bloqueada') {
          const d = e.detalhes ?? {};
          const a = d.avaliacao as { id?: unknown; title?: unknown } | undefined;
          /*
           * O `preRequisito` do 403 traz `tipo`/`id`/`title`, que é a gramática
           * dos `details` desta casa desde antes da tranca de ordem existir. O
           * mapeamento é aqui, num sítio só: renomear no servidor mexeria num
           * corpo que outras telas já leem, e deixar duas grafias a circular
           * seria pior do que estas três linhas.
           */
          const pr = d.preRequisito as Record<string, unknown> | undefined;
          setTranca({
            frase: e.message,
            /* A frase do 403 entra como RESERVA do lançamento: se o `liberaEm`
               vier, a tela deriva a dela e esta fica para quem não consegue. */
            lancamento:
              d.motivo === 'hora_marcada'
                ? lerLancamento({ em: d.liberaEm, frase: e.message })
                : null,
            avaliacao:
              a && typeof a.id === 'string' && typeof a.title === 'string'
                ? { id: a.id, title: a.title }
                : null,
            dependencia: pr
              ? lerTranca({ ...pr, exigeTipo: pr.tipo, exigeId: pr.id, titulo: pr.title })
              : null,
          });
          return;
        }
        setErro(e instanceof Error ? e.message : t('estado.naoDeuParaCarregar'));
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { dados, erro, oferta, tranca };
}
